// src/lib/agent-journee-regles.js — LES RÈGLES DE « MON PLANNING », d'après la
// maquette NEST v677 (espace agent, journée restructurée du 28/10). Helpers purs.
//
// La journée : PROCHAIN (une carte, en grand) · ENSUITE · N (repliées) · FAIT · N
// (repliées). L'ordre : ménages et interventions à leur heure, puis les reprises,
// puis les contrôles.
//
// Les quatre tuiles : un chiffre, une phrase, une couleur (vert = fini, aubergine
// = il en reste). Une lecture ratée s'affiche « — », jamais 0.

import { formaterHeureCourte, minutesDeHeure } from './menage-frise'

export const SANS_NOM = "un logement qui n'est plus le vôtre"

const SANS_HEURE_INTERVENTION = 9e8
const REPRISE = 8e9
const CONTROLE = 9e9

function pluriel(n, mot) { return `${mot}${n > 1 ? 's' : ''}` }

/** « Dimanche 23 août » : la tête de l'écran. */
export function dateTitreFR(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  const s = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris',
  }).format(t)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** « 16h » depuis « 16:00 » ; null sans heure. */
export function heureCourte(hhmm) {
  const m = minutesDeHeure(typeof hhmm === 'string' ? hhmm.slice(0, 5) : hhmm)
  return m == null ? null : formaterHeureCourte(m)
}

/** « 11h40 » depuis un horodatage, à l'heure de Paris ; null sinon. */
export function heureFinParis(iso) {
  const t = Date.parse(iso || '')
  if (Number.isNaN(t)) return null
  const [h, m] = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Paris',
  }).format(t).split(':').map(Number)
  return formaterHeureCourte(h * 60 + m)
}

/**
 * LA RANGÉE DE LA JOURNÉE. `taches` : { cle, type, min, fini, ... }, où `min`
 * vaut les minutes depuis minuit (null sans heure).
 * @returns {{prochain:object|null, ensuite:object[], fait:object[]}}
 */
export function rangerJournee(taches) {
  const rang = (t) => {
    if (t.type === 'controle') return CONTROLE
    if (t.type === 'reprise') return REPRISE
    return Number.isFinite(t.min) ? t.min : SANS_HEURE_INTERVENTION
  }
  const tri = (Array.isArray(taches) ? taches : []).slice().sort((a, b) => rang(a) - rang(b))
  const aFaire = tri.filter((t) => !t.fini)
  return { prochain: aFaire[0] || null, ensuite: aFaire.slice(1), fait: tri.filter((t) => t.fini) }
}

/** Le titre de la première carte : ce qu'on attend de lui, tout de suite. */
export function titreProchain(tache) {
  if (tache?.type === 'controle') return 'À contrôler'
  if (tache?.type === 'reprise') return 'À reprendre'
  return 'Prochain'
}

/**
 * LES QUATRE TUILES. Chaque compteur vaut null s'il n'a pas pu être lu.
 * Maquette : « ménages » et « contrôle » toujours là ; « à reprendre » et
 * « changements » n'existent que s'il y en a.
 */
export function tuilesBandeau({ menages, termines, controles, avantControle, reprises, logementReprise, changements }) {
  const inconnu = (n) => n == null || !Number.isFinite(Number(n))
  const tuiles = []
  if (inconnu(menages)) {
    tuiles.push({ cle: 'men', chiffre: '—', texte: 'ménages', ton: 'gr' })
  } else {
    const reste = menages - termines
    tuiles.push({ cle: 'men', chiffre: String(menages), ton: reste === 0 ? 'ok' : 'ab',
      texte: `${pluriel(menages, 'ménage')} · ${termines} ${pluriel(termines, 'terminé')} · ${reste} à faire` })
  }
  if (inconnu(controles)) {
    tuiles.push({ cle: 'ctl', chiffre: '—', texte: 'contrôle', ton: 'gr' })
  } else {
    tuiles.push({ cle: 'ctl', chiffre: String(controles), ton: controles === 0 ? 'ok' : 'ab',
      texte: `${pluriel(controles, 'contrôle')} · ${controles === 0 ? 'rien en attente' : avantControle || "aujourd'hui"}` })
  }
  if (inconnu(reprises)) {
    tuiles.push({ cle: 'rep', chiffre: '—', texte: 'à reprendre', ton: 'gr' })
  } else if (reprises > 0) {
    tuiles.push({ cle: 'rep', chiffre: String(reprises), ton: 'ab',
      texte: `à reprendre${logementReprise ? ` · ${logementReprise}` : ''}` })
  }
  if (inconnu(changements)) {
    tuiles.push({ cle: 'chg', chiffre: '—', texte: 'changements', ton: 'gr' })
  } else if (changements > 0) {
    tuiles.push({ cle: 'chg', chiffre: String(changements), ton: 'ab',
      texte: `${pluriel(changements, 'changement')} · dans votre planning` })
  }
  return tuiles
}

const LIBELLES = { retrait: 'Retiré', ajout: 'Nouveau', heure: 'Horaire modifié', date: 'Déplacé à cette date' }

/** Une ligne du panneau « changements » : le mot, le logement, le jour. */
export function ligneChangement(e) {
  const t = Date.parse(`${String(e?.date || '').slice(0, 10)}T12:00:00Z`)
  const jour = Number.isNaN(t) ? '' : new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris',
  }).format(t)
  return {
    mot: LIBELLES[e?.type] || 'Changement',
    alerte: e?.type === 'retrait',
    texte: [e?.logement?.nom || SANS_NOM, jour].filter(Boolean).join(' · '),
  }
}
